<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { centsToWholeUnits, formatWholeNumber, initials } from '@/utils/poolaReportFormat.js';

const props = defineProps({
  // { supplierId, supplierName, completedOfferCount, poolcreditReturnedCents,
  //   unitsSold, rank, isViewer } — see useSupplierDealReport's composeLeaderboard.
  row: { type: Object, required: true },
});

const { t } = useI18n({ useScope: 'global' });

const displayName = computed(() => props.row.supplierName ?? '—');
</script>

<template>
  <div
    class="report-lb-row"
    :class="{ 'report-lb-row--me': row.isViewer }"
    data-testid="report-leaderboard-row"
    :data-is-viewer="row.isViewer ? 'true' : 'false'"
  >
    <span class="report-lb-row__rank num">{{ formatWholeNumber(row.rank) }}</span>
    <span class="report-lb-row__avatar" aria-hidden="true">{{ initials(row.supplierName) }}</span>
    <div class="report-lb-row__who">
      <p class="report-lb-row__name">{{ displayName }}</p>
      <div class="report-lb-row__metrics">
        <span class="report-lb-row__metric">
          <span>{{ t('poola.report.leaderboard.columns.completedOffers') }}</span>
          <b class="num">{{ formatWholeNumber(row.completedOfferCount) }}</b>
        </span>
        <span class="report-lb-row__metric">
          <span>{{ t('poola.report.leaderboard.columns.poolcredit') }}</span>
          <b class="num">{{ centsToWholeUnits(row.poolcreditReturnedCents) }}</b>
        </span>
        <span class="report-lb-row__metric">
          <span>{{ t('poola.report.leaderboard.columns.unitsSold') }}</span>
          <b class="num">{{ formatWholeNumber(row.unitsSold) }}</b>
        </span>
      </div>
    </div>
    <span v-if="row.isViewer" class="report-lb-row__me-tag">{{
      t('poola.report.leaderboard.ownRowTag')
    }}</span>
  </div>
</template>

<style scoped>
.num {
  font-variant-numeric: tabular-nums;
}

.report-lb-row {
  display: flex;
  align-items: center;
  gap: 11px;
  background: var(--report-surface);
  border: 1px solid var(--report-hairline);
  border-radius: 13px;
  padding: 10px 12px;
}

.report-lb-row--me {
  border: 1.5px solid var(--report-teal);
  background: var(--report-teal-bg);
}

.report-lb-row__rank {
  flex: none;
  width: 22px;
  text-align: center;
  font-size: 13px;
  font-weight: var(--poolit-font-weight-bold, 700);
  color: var(--report-text-mute);
}

.report-lb-row--me .report-lb-row__rank {
  color: var(--report-teal-deep);
}

.report-lb-row__avatar {
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--report-surface-2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: var(--poolit-font-weight-bold, 700);
  color: var(--report-text-soft);
}

.report-lb-row--me .report-lb-row__avatar {
  background: var(--report-teal);
  color: #fff;
}

.report-lb-row__who {
  min-width: 0;
  flex: 1;
}

.report-lb-row__name {
  margin: 0;
  font-size: 13px;
  font-weight: var(--poolit-font-weight-bold, 700);
  color: var(--report-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-lb-row__metrics {
  margin-top: 2px;
  font-size: 10.5px;
  color: var(--report-text-soft);
  display: flex;
  gap: 9px;
  flex-wrap: wrap;
}

.report-lb-row__metric {
  display: inline-flex;
  gap: 4px;
}

.report-lb-row__metrics b {
  color: var(--report-text);
  font-weight: var(--poolit-font-weight-bold, 700);
}

.report-lb-row__me-tag {
  flex: none;
  font-size: 10.5px;
  font-weight: var(--poolit-font-weight-bold, 700);
  color: var(--report-teal-deep);
  background: var(--report-surface);
  border-radius: var(--poolit-radius-full, 9999px);
  padding: 3px 8px;
}
</style>
